import { socket } from '@/api/socket';
import { LogoLoader } from '@/components/logo-loader';
import { isDevMode } from '@/lib/is-dev-mode';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { extractPageItems } from './client-virtual-feed-utils';

const LIMIT = 50;
const SCROLL_COMPENSATION_THRESHOLD = 80;
const SEARCH_DEBOUNCE_MS = 250;

export interface ClientVirtualFeedPage<T> {
	clientId: string;
	cursor: string | null;
	hasMore: boolean;
	items: T[];
}

export interface UseClientVirtualFeedOptions<T> {
	clientId: string;
	searchText: string;
	enabled: boolean;
	fetchEvent: string;
	responseEvent: string;
	items: T[];
	setItems: (items: T[]) => void;
	appendItems: (items: T[]) => void;
	getItemId: (item: T) => string | number;
	estimateSize?: number;
	extraQuery?: Record<string, unknown>;
	emptyLabel: string;
	emptySearchLabel?: string;
	columns?: number;
	renderRow?: (rowItems: T[], rowIndex: number) => ReactNode;
	renderItem: (item: T, index: number) => ReactNode;
	devMockFetch?: (params: { cursor: string | null; searchText: string }) => ClientVirtualFeedPage<T>;
}

type InFlightRequest = {
	generation: number;
	cursor: string | null | undefined;
	mode: 'refresh' | 'append';
};

export function useClientVirtualFeed<T>({
	clientId,
	searchText,
	enabled,
	fetchEvent,
	responseEvent,
	items,
	setItems,
	appendItems,
	getItemId,
	estimateSize = 140,
	extraQuery,
	emptyLabel,
	emptySearchLabel = 'Ничего не найдено',
	columns = 1,
	renderRow,
	renderItem,
	devMockFetch,
}: UseClientVirtualFeedOptions<T>) {
	const listRef = useRef<HTMLDivElement | null>(null);
	const prevCountRef = useRef(0);
	const prevFirstIdRef = useRef<string | number | null>(null);
	const appendModeRef = useRef(false);
	const cursorRef = useRef<string | null>(null);
	const fetchGenerationRef = useRef(0);
	const inFlightRef = useRef<InFlightRequest | null>(null);
	const firstFetchedRef = useRef(false);
	const queryPayloadRef = useRef<Record<string, unknown>>({});
	const devMockFetchRef = useRef(devMockFetch);
	const setItemsRef = useRef(setItems);
	const appendItemsRef = useRef(appendItems);

	devMockFetchRef.current = devMockFetch;
	setItemsRef.current = setItems;
	appendItemsRef.current = appendItems;

	const [cursor, setCursor] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [firstFetched, setFirstFetched] = useState(false);
	const [debouncedSearch, setDebouncedSearch] = useState(searchText);
	const [fetchKey, setFetchKey] = useState('');

	const currentFetchKey = useMemo(
		() => `${clientId}:${debouncedSearch}:${JSON.stringify(extraQuery ?? {})}`,
		[clientId, debouncedSearch, extraQuery],
	);

	const queryPayload = useMemo(
		() => ({
			clientId,
			limit: LIMIT,
			searchText: debouncedSearch.trim() || undefined,
			...extraQuery,
		}),
		[clientId, debouncedSearch, extraQuery],
	);

	useEffect(() => {
		queryPayloadRef.current = queryPayload;
	}, [queryPayload]);

	useEffect(() => {
		cursorRef.current = cursor;
	}, [cursor]);

	useEffect(() => {
		firstFetchedRef.current = firstFetched;
	}, [firstFetched]);

	const useRowLayout = columns > 1 && Boolean(renderRow);
	const rowCount = useRowLayout ? Math.ceil(items.length / columns) : items.length;
	const virtualCount = useRowLayout ? rowCount : items.length;

	const virtualizer = useVirtualizer({
		count: virtualCount,
		getScrollElement: () => listRef.current,
		estimateSize: () => estimateSize,
		overscan: 8,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useEffect(() => {
		const timeout = setTimeout(() => setDebouncedSearch(searchText), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timeout);
	}, [searchText]);

	const emitRefresh = useCallback(() => {
		if (!socket?.connected) return false;
		fetchGenerationRef.current += 1;
		inFlightRef.current = {
			generation: fetchGenerationRef.current,
			cursor: undefined,
			mode: 'refresh',
		};
		appendModeRef.current = false;
		setIsFetching(true);
		socket.emit(fetchEvent, { ...queryPayloadRef.current, cursor: undefined });
		return true;
	}, [fetchEvent]);

	useEffect(() => {
		if (!enabled || isDevMode) return;
		if (!socket) return;

		function handleResponse(data: Record<string, unknown>) {
			if (data.clientId !== clientId) return;

			const inFlight = inFlightRef.current;
			if (!inFlight || inFlight.generation !== fetchGenerationRef.current) return;

			setIsFetching(false);
			setFirstFetched(true);

			const resolvedItems = extractPageItems<T>(data);
			if (inFlight.mode === 'append') {
				appendItemsRef.current(resolvedItems);
			} else {
				setItemsRef.current(resolvedItems);
			}
			const nextCursor = (data.cursor as string | null) ?? null;
			setCursor(nextCursor);
			cursorRef.current = nextCursor;
			setHasMore(Boolean(data.hasMore));
			appendModeRef.current = false;
			inFlightRef.current = null;
		}

		socket.on(responseEvent, handleResponse);
		return () => {
			socket.off(responseEvent, handleResponse);
		};
	}, [enabled, clientId, responseEvent]);

	useEffect(() => {
		if (!enabled || isDevMode || !socket) return;

		function onConnect() {
			if (!firstFetchedRef.current) {
				emitRefresh();
			}
		}

		socket.on('connect', onConnect);
		return () => {
			socket.off('connect', onConnect);
		};
	}, [enabled, emitRefresh]);

	useEffect(() => {
		if (!enabled) return;

		if (fetchKey === currentFetchKey) {
			return;
		}

		setFetchKey(currentFetchKey);
		setFirstFetched(false);
		appendModeRef.current = false;
		setCursor(null);
		cursorRef.current = null;
		setHasMore(false);
		if (listRef.current) listRef.current.scrollTop = 0;

		const mockFetch = devMockFetchRef.current;
		if (isDevMode && mockFetch) {
			setIsFetching(true);
			const page = mockFetch({ cursor: null, searchText: debouncedSearch });
			setItemsRef.current(page.items);
			setCursor(page.cursor);
			cursorRef.current = page.cursor;
			setHasMore(page.hasMore);
			setIsFetching(false);
			setFirstFetched(true);
			return;
		}

		setItemsRef.current([]);
		if (!emitRefresh()) return;
	}, [enabled, currentFetchKey, emitRefresh]);

	useLayoutEffect(() => {
		const firstId = items.length > 0 ? getItemId(items[0]) : null;
		const isPrepend =
			items.length === prevCountRef.current + 1 && prevCountRef.current > 0 && firstId !== prevFirstIdRef.current;

		if (isPrepend && listRef.current) {
			const el = listRef.current.querySelector('[data-index="0"]') as HTMLElement | null;
			const height = el?.getBoundingClientRect().height ?? estimateSize;

			if (listRef.current.scrollTop > SCROLL_COMPENSATION_THRESHOLD) {
				listRef.current.scrollTop += height + 8;
			}
		}

		prevCountRef.current = items.length;
		prevFirstIdRef.current = firstId;
	}, [items, estimateSize, getItemId]);

	useEffect(() => {
		if (!enabled || !hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			const loadThreshold = useRowLayout ? Math.max(0, rowCount - 5) : items.length - 10;
			if (lastItem.index >= loadThreshold) {
				const mockFetch = devMockFetchRef.current;
				if (isDevMode && mockFetch) {
					setIsFetching(true);
					appendModeRef.current = true;
					const page = mockFetch({ cursor: cursorRef.current, searchText: debouncedSearch });
					appendItemsRef.current(page.items);
					setCursor(page.cursor);
					cursorRef.current = page.cursor;
					setHasMore(page.hasMore);
					setIsFetching(false);
					return;
				}

				if (socket?.connected) {
					setIsFetching(true);
					appendModeRef.current = true;
					inFlightRef.current = {
						generation: fetchGenerationRef.current,
						cursor: cursorRef.current,
						mode: 'append',
					};
					socket.emit(fetchEvent, {
						...queryPayload,
						cursor: cursorRef.current ?? undefined,
					});
				}
			}
		}
	}, [virtualItems, items.length, rowCount, useRowLayout, isFetching, hasMore, queryPayload, enabled, fetchEvent, debouncedSearch]);

	const isSearching = debouncedSearch.trim().length > 0;

	const content =
		!firstFetched || (isFetching && items.length === 0) ? (
			<div className='flex items-center justify-center mt-30'>
				<LogoLoader />
			</div>
		) : items.length === 0 ? (
			<div className='text-center mt-30 text-sm text-muted-foreground'>{isSearching ? emptySearchLabel : emptyLabel}</div>
		) : (
			<div
				style={{
					height: `${virtualizer.getTotalSize()}px`,
					width: '100%',
					position: 'relative',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
					}}
				>
					{virtualItems.map(({ index, key }) => (
						<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
							{useRowLayout && renderRow
								? renderRow(items.slice(index * columns, index * columns + columns), index)
								: renderItem(items[index], index)}
						</div>
					))}
				</div>
			</div>
		);

	return {
		listRef,
		content,
		isFetching,
		isSearching,
	};
}
